import { logV } from "../../../shared/services/debug-logger";
import {
  VESSEL_CARD_ZN_SCENARIOS,
  VESSEL_CARD_ZN_STANDARD_CONDITION,
  VESSEL_CARD_ZN_UNAVAILABLE_REASONS,
} from "../constants/vessel-card-zn.constants";
import { useVesselCardZn } from "../hooks/useVesselCardZn";
import type { VesselCardZnPanelProps, VesselCardZnScenario } from "../models/vessel-card-zn.models";
import { formatVesselCardMetric } from "../utils/vessel-card-baltic.utils";
import { formatVesselCardZnDelta } from "../utils/vessel-card-zn.utils";
import VesselCardZnChart from "./VesselCardZnChart";
import { twMerge } from "tailwind-merge";

// Compares exact stored ZN points with Expected Performance while keeping unknown training freshness visible.
// The source version date describes the stored data; it must never be presented as a model training date.
const VesselCardZnPanel = ({ imo, performance, view, fuelGrade }: VesselCardZnPanelProps) => {
  const model = useVesselCardZn(imo, performance);
  logV("VesselCardZnPanel - BUILD", { loading: model.loading, modelState: model.modelState });
  const conditionLabel = model.useMockData ? VESSEL_CARD_ZN_STANDARD_CONDITION : "16 kn / 1.25 m swell";
  const hasMissingPredictions =
    model.modelState === "available" && model.rows.some((row) => row.modelConsumption === null);
  const statusLabel = model.loading
    ? model.useMockData ? "Loading model…" : "Loading stored points…"
    : model.error || model.modelState !== "available"
      ? "Not Available"
      : model.useMockData ? "Model available" : "Stored points available";
  const statusDescription = model.loading
    ? "Loading ZN points at Expected Performance speeds."
    : model.error
      ? "ZN fuel model data is Not Available. Expected Performance remains visible."
      : model.modelState === "stale"
        ? "Last training is older than 90 days. ZN fuel model data is Not Available."
        : model.modelState === "unavailable"
          ? `${VESSEL_CARD_ZN_UNAVAILABLE_REASONS[model.response?.reason ?? ""] ?? "No eligible model for this IMO."} Expected Performance remains visible.`
          : hasMissingPredictions
            ? "Some predictions or expected inputs are missing. Affected ZN values and deltas are Not Available."
            : `${model.useMockData ? "Mock predictions" : "Stored ZN points"} at Expected Performance speeds.`;

  return (
    <div data-test="vessel-card-zn-comparison">
      {model.useMockData && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <p data-test="vessel-card-zn-mock-banner">
            <strong>ZN mock data.</strong> Synthetic predictions for demonstration; not connected to ZeroNorth.
          </p>
          <label className="mt-2 flex flex-wrap items-center gap-2 font-semibold">
            Mock scenario
            <select
              value={model.scenario}
              onChange={(event) => model.setScenario(event.currentTarget.value as VesselCardZnScenario)}
              data-test="vessel-card-zn-scenario-select"
              className="rounded-md border border-amber-300 bg-white px-2 py-1 text-xs text-gray-900 dark:border-amber-700 dark:bg-gray-900 dark:text-gray-100"
            >
              {VESSEL_CARD_ZN_SCENARIOS.map((scenario) => (
                <option key={scenario.value} value={scenario.value}>
                  {scenario.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <div
        className="mb-3 grid gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/30 sm:grid-cols-2"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-test="vessel-card-zn-model-status"
      >
        <div>
          <span
            className={twMerge(
              "inline-block rounded-full px-3 py-1 text-xs font-semibold",
              model.loading
                ? "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                : model.modelState === "available" && !model.error
                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
                  : "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200",
            )}
          >
            {statusLabel}
          </span>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            ZN Fuel Model · {model.useMockData ? "mock connector" : "Stored ZN / Snowflake"}
          </p>
        </div>
        <dl className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <dt className="text-gray-500 dark:text-gray-400">
              {model.useMockData ? "Standard condition" : "Stored condition"}
            </dt>
            <dd className="mt-1 font-semibold">
              {conditionLabel}
              {model.useMockData ? " · 16 kn / 1.25 m" : ""}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">
              {model.useMockData ? "Last model training" : "Last stored update"}
            </dt>
            <dd
              className="mt-1 font-semibold"
              data-test={model.useMockData ? "vessel-card-zn-training-date" : "vessel-card-zn-source-date"}
            >
              {(model.useMockData ? model.trainedAt : model.response?.sourceUpdatedAt?.slice(0, 10)) ?? "Not Available"}
            </dd>
          </div>
        </dl>
        {model.response?.segment && (
          <p
            className="text-xs text-gray-600 dark:text-gray-300 sm:col-span-2"
            data-test="vessel-card-zn-draft-reference"
          >
            {model.response.segment} reference drafts (fore = aft): Ballast {model.response.ballastDraft} m · Laden{" "}
            {model.response.ladenDraft} m.
          </p>
        )}
        <p className="text-xs text-gray-600 dark:text-gray-300 sm:col-span-2">
          Default ZN baseline · Main engine + normal sailing auxiliary · Boiler excluded.
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-300 sm:col-span-2">{statusDescription}</p>
        {!model.useMockData && (
          <p className="text-xs text-gray-600 dark:text-gray-300 sm:col-span-2" data-test="vessel-card-zn-training-freshness">
            Training freshness is unverified. Last stored update is a source version date, not a model training date.
          </p>
        )}
      </div>

      {view === "table" ? (
        <div className="overflow-x-auto">
          <table
            className="w-full min-w-[580px] border-separate border-spacing-0 text-sm"
            data-test="vessel-card-zn-table"
            aria-describedby="vessel-card-zn-delta-help"
          >
            <caption className="sr-only">
              Expected Performance versus {model.useMockData ? "mock ZN" : "ZN"} fuel predictions for IMO {imo}; speed
              in knots, consumption in tonnes per day.
            </caption>
            <thead className="text-xs text-gray-500 dark:text-gray-400">
              <tr>
                <th rowSpan={2} scope="col" className="pb-2 pr-3 text-left font-medium">
                  Mode
                  <span className="mt-1 block text-amber-600 dark:text-amber-400" data-test="vessel-card-zn-fuel-grade">
                    Fuel grade: {fuelGrade ?? "Not available"}
                  </span>
                </th>
                <th colSpan={2} scope="colgroup" className="border-l border-gray-200 px-2 pb-1 dark:border-gray-700">
                  Expected Performance
                </th>
                <th colSpan={2} scope="colgroup" className="border-l border-gray-200 px-2 pb-1 dark:border-gray-700">
                  ZN Fuel Model
                  <br />({conditionLabel})
                </th>
                <th rowSpan={2} scope="col" className="border-l border-gray-200 px-2 pb-2 dark:border-gray-700">
                  Expected Perf vs ZN Fuel Model Cons %
                </th>
              </tr>
              <tr>
                {["Speed (kn)", "Cons (t/day)", "Speed (kn)", "Cons (t/day)"].map((label, index) => (
                  <th
                    key={index}
                    scope="col"
                    className={twMerge(
                      "px-2 pb-2 text-xs font-medium",
                      index % 2 === 0 && "border-l border-gray-200 dark:border-gray-700",
                    )}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {model.rows.map((row) => (
                <tr key={row.mode} data-test={`vessel-card-zn-row-${row.mode.toLowerCase().replaceAll(" ", "-")}`}>
                  <th scope="row" className="whitespace-nowrap py-3 pr-3 text-left font-semibold">
                    {row.mode}
                  </th>
                  {[row.expectedSpeed, row.expectedConsumption, row.modelSpeed, row.modelConsumption].map(
                    (value, index) => (
                      <td
                        key={index}
                        className={twMerge(
                          "px-2 py-3 text-center",
                          index % 2 === 0 && "border-l border-gray-200 dark:border-gray-700",
                        )}
                      >
                        {index >= 2 && value === null ? "Not Available" : formatVesselCardMetric(value)}
                      </td>
                    ),
                  )}
                  <td className="border-l border-gray-200 px-2 py-3 text-center text-xs font-semibold dark:border-gray-700">
                    {row.deltaPercent === null ? "Not Available" : formatVesselCardZnDelta(row.deltaPercent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700" data-test="vessel-card-zn-graph">
          <h3 className="mb-2 text-sm font-semibold text-teal-600 dark:text-teal-300">
            Expected Performance vs ZN Fuel Model
          </h3>
          <p
            className="mb-3 text-xs font-semibold text-amber-600 dark:text-amber-400"
            data-test="vessel-card-zn-fuel-grade"
          >
            Fuel grade: {fuelGrade ?? "Not available"}
          </p>
          <div className="grid gap-4 lg:grid-cols-2">
            <VesselCardZnChart
              condition="Ballast"
              rows={model.rows}
              source={model.useMockData ? "mock" : "snowflake"}
            />
            <VesselCardZnChart condition="Laden" rows={model.rows} source={model.useMockData ? "mock" : "snowflake"} />
          </div>
          <p
            className="mt-3 text-xs leading-5 text-gray-500 dark:text-gray-400"
            data-test="vessel-card-zn-curve-disclaimer"
          >
            Each plot scales independently. The Expected line uses the workbook's 2.8 power law, 2 t/day auxiliary
            allowance, 0.1 kn steps and explicit endpoint values. The dashed ZN line connects available{" "}
            {model.useMockData ? "mock " : ""}predictions; ZN totals use normal sailing auxiliary consumption
            separately.
          </p>
        </div>
      )}
      <p id="vessel-card-zn-delta-help" className="mt-3 text-xs leading-5 text-gray-500 dark:text-gray-400">
        Comparison = (ZN − Expected) / Expected × 100. Positive means Expected Performance consumes less than ZN at the
        same speed; negative means more.{" "}
        {model.useMockData
          ? "Mock scenarios apply an inclusive 90-day training-date cutoff."
          : "Stored comparisons use exact speed, draft and weather values; missing or ambiguous points remain Not Available."}
      </p>
    </div>
  );
};

export default VesselCardZnPanel;
